import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import type { CapacityLine } from '../../utils/dashboard';
import { formatMinutes } from '../../utils/dashboard';
import { dashboardCopy } from '../../copy/dashboard';

// ---------------------------------------------------------------------------
// CapacityStrip — one dense line, no chart. Three numbers and a proportional
// bar so the shape of the day reads at a glance. If the day is overloaded
// the bar fills its full width and the RiskStrip carries the severity.
// ---------------------------------------------------------------------------

interface CapacityStripProps {
  capacity: CapacityLine | null;
  loading?: boolean;
}

export function CapacityStrip({ capacity, loading = false }: CapacityStripProps) {
  if (loading) {
    return <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />;
  }
  if (!capacity) return null;

  const total = capacity.committedMinutes + capacity.protectedMinutes + capacity.slackMinutes;
  const safeTotal = total > 0 ? total : 1; // Avoid div-by-zero in rare empty plans.

  const segments = [
    { key: 'committed', label: dashboardCopy.capacityCommitted, value: capacity.committedMinutes, colour: 'primary.main' },
    { key: 'protected', label: dashboardCopy.capacityProtected, value: capacity.protectedMinutes, colour: 'status.onPlan' },
    { key: 'slack', label: dashboardCopy.capacitySlack, value: capacity.slackMinutes, colour: 'divider' },
  ];

  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      {/* Numeric summary — the primary information. */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2.5, flexWrap: 'wrap' }}>
        {segments.map((seg) => (
          <Box key={seg.key} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMinutes(seg.value)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {seg.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Proportional bar — visual rhythm, not an axis to read exactly. */}
      <Box sx={{ mt: 1.25, display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden', bgcolor: 'action.hover' }}>
        {segments.map((seg) => (
          <Box
            key={seg.key}
            aria-hidden
            sx={{
              width: `${(seg.value / safeTotal) * 100}%`,
              bgcolor: seg.colour,
              transition: 'width 200ms',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default CapacityStrip;
